import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { plansManagement } from "../../../../../../hooks/api/PlansManagement/PlansManagement";
import { languagesManagement } from "../../../../../../hooks/api/LanguagesManagement";
import { languageProgramsManagement } from "../../../../../../hooks/api/LanguageProgramsManagement";
import { prepareSelect } from "../../../../../../services/core/Select";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";
import { Select } from "../../../../../../themes/Default/components/Form/components/Select";
import SummerNote from "../../../../../../packages/SummerNote/SummerNote";
import { useUserToken } from "../../../../../../hooks/core/UserToken";
import { roles } from "../../../../../../services/utils/Roles";

const EditPlan = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { planId } = useParams();
    const [formErrors, setFormErrors] = useState([]);
    const { useUpdatePlansMutation, useViewPlansMutation } = plansManagement;
    const [updatePlan, { isLoading: isUpdateingPlan }] = useUpdatePlansMutation();
    const [viewPlan] = useViewPlansMutation();
    const { useLanguageListMutation } = languagesManagement;
    const [languageList] = useLanguageListMutation();
    const [languages, setLanguages] = useState([]);
    const { useProgramListMutation } = languageProgramsManagement;
    const [languageProgram, setLanguageProgram] = useState([]);
    const [languageProgramList] = useProgramListMutation();
    const [saving, setSaving] = useState(false);

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    const isProfessorCoordinator = decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR);

    const tabs = isProfessorCoordinator
    ? [
        { title: t("plans.title"), isActive: true }
      ]
    : [
        { title: t("plans.title"), isActive: true },
        { title: t("groupTypes.title"), onClick: () => gotoTypeTab() },
        { title: t("groups.progress"), onClick: () => gotoProgressTab() },
        { title: t("groups.progressTest"), onClick: () => gotoProgressTestTab() },
    ];

    const gotoTypeTab = () => {
        navigate("/settings/groups/types");
    };

    const gotoProgressTab = () => {
        navigate("/settings/groups/progress");
    };

    const gotoProgressTestTab = () => {
        navigate("/settings/groups/progress-tests");
    };

    const onSave = () => {
        if (formRef.current) {
            setSaving(true);
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = { ...values };
        updatePlan({ body, planId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                navigate("/settings/groups/plans");
                toast.info(result?.data?.info?.responseMessage);
            }
            setSaving(false);
        });
    };

    useEffect(() => {
        viewPlan(planId).then((result) => {
            formRef.current.setValues(result?.data);
            console.log(result?.data);

            languageProgramList({ language: result?.data?.language }).then((result) => {
                setLanguageProgram(prepareSelect(result?.data?.records));
            });
        });
    }, []);

    useEffect(() => {
        languageList().then((result) => {
            setLanguages(prepareSelect(result?.data?.records));
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings/groups/plans" />
                <Button title={t("save")} isDisabled={isUpdateingPlan} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("groups.title")} />
                    <Link title={t("plans.title")} />
                    <Link title={t("plans.editOne")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        classNumber: null,
                        description: "",
                        language: null,
                        languageProgram: null,
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select
                                        name="language"
                                        label={t("program.language")}
                                        required={true}
                                        options={languages}
                                        onChange={(event) => {
                                            setFieldValue("language", event.target.value);
                                            languageProgramList({ language: event.target.value }).then((result) => {
                                                setLanguageProgram(prepareSelect(result?.data?.records));
                                            });
                                        }}
                                        selectedValue={values.language}
                                        error={formErrors?.language}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="languageProgram"
                                        label={t("groups.languageProgram")}
                                        onChange={handleChange}
                                        required={true}
                                        options={languageProgram}
                                        selectedValue={values.languageProgram}
                                        error={formErrors?.program}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="classNumber"
                                        label={t("plans.classNumber")}
                                        required={true}
                                        value={values.classNumber}
                                        onChange={handleChange}
                                        error={formErrors?.classNumber}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        saving={saving}
                                        required={true}
                                        height="550px"
                                        name="description"
                                        label={t("plans.description")}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={formErrors?.description}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditPlan;
